/* global globalThis */

export const DLOCAL_TENANT_ID = "80ca91bd-bc50-40c0-90b7-f0e65294d10e";
export const requiresEmailEventId = (tenantId) => tenantId === DLOCAL_TENANT_ID;

export const requireEmailEventId = (tenantId, eventId) => {
  if (requiresEmailEventId(tenantId) && (typeof eventId !== "string" || !eventId.trim())) {
    throw new Error("A stable email event ID is required for this tenant.");
  }
};

export const createEmailEventId = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const resultEntries = (result) => {
  if (Array.isArray(result?.results) && result.results.length) return result.results;
  if (Array.isArray(result?.result?.results) && result.result.results.length) return result.result.results;
  return [result || {}];
};
const count = (value) => typeof value === "number" && Number.isFinite(value) ? value : null;
const sentCount = (entry) => count(entry?.sent) ?? count(entry?.delivery?.sent) ?? (entry?.sent === true ? 1 : entry?.sent === false ? 0 : null);
const duplicateCount = (entry) => count(entry?.duplicate) ?? count(entry?.delivery?.duplicate) ?? 0;
const requiresReconciliation = (entry) => entry?.delivery?.status === "reconciliation_required" || entry?.status === "reconciliation_required";
const isSkipped = (entry) => entry?.delivery?.status === "skipped" || entry?.status === "skipped";
const hasExplicitFailure = (entry) => !!entry?.error || (count(entry?.failed) || 0) > 0 || (count(entry?.delivery?.failed) || 0) > 0
  || (Array.isArray(entry?.failures) && entry.failures.length > 0) || (Array.isArray(entry?.delivery?.failures) && entry.delivery.failures.length > 0);
const isDuplicateOnly = (entry) => duplicateCount(entry) > 0 && sentCount(entry) === 0 && entry?.delivery?.status === "complete";
const isFailed = (entry) => hasExplicitFailure(entry) || (entry?.success === false && !isSkipped(entry) && !isDuplicateOnly(entry));
const isTerminal = (entry) => !requiresReconciliation(entry) && !isFailed(entry) && (
  isSkipped(entry) || entry?.delivery?.status === "complete" || (sentCount(entry) || 0) > 0 || duplicateCount(entry) > 0 || entry?.success === true
);

// One random ID per logical action, retained after failure or an uncertain outcome.
// Payload comparison identifies a retry; message content never determines the ID.
// The record also holds resolved recipients so an all-users retry uses the original list.
export const createEmailActionRunner = (makeId = createEmailEventId) => {
  const actions = new Map();
  return ({ tenantId, action, payload, request }) => {
    const key = JSON.stringify([tenantId, action]);
    const signature = JSON.stringify(payload);
    let record = actions.get(key);
    if (record?.pending) {
      if (record.signature === signature) return record.pending;
      return Promise.reject(new Error("Wait for the current email request to finish."));
    }
    if (!record || record.signature !== signature) {
      record = { signature, payload: JSON.parse(signature), eventId: requiresEmailEventId(tenantId) ? makeId() : undefined, context: {} };
      actions.set(key, record);
    }
    const current = record;
    // Defer the callback until pending is assigned, protecting same-tick clicks.
    current.pending = Promise.resolve().then(() => request({
      ...current.payload,
      ...(current.eventId ? { eventId: current.eventId } : {}),
    }, current.context)).then((result) => {
      if (!requiresEmailEventId(tenantId) || resultEntries(result).every(isTerminal)) actions.delete(key);
      return result;
    }).finally(() => { current.pending = null; });
    return current.pending;
  };
};

export const emailActionOutcome = (result, label = "Email") => {
  const entries = resultEntries(result);
  const knownSent = entries.some((entry) => sentCount(entry) !== null);
  const sent = entries.reduce((total, entry) => total + (sentCount(entry) || 0), 0);
  const duplicate = entries.reduce((total, entry) => total + duplicateCount(entry), 0);
  const suppressed = entries.reduce((total, entry) => total + (count(entry?.suppressed) ?? count(entry?.delivery?.suppressed) ?? count(entry?.delivery?.skipped) ?? 0), 0);
  const uncertain = entries.some(requiresReconciliation);
  const failed = entries.some(isFailed);
  const noRecipients = entries.some((entry) => entry?.reason === "no_opted_in_recipients" || entry?.delivery?.reason === "no_opted_in_recipients");
  const details = [knownSent ? `${sent}${uncertain ? " confirmed" : ""} sent` : null, duplicate ? `${duplicate} duplicate${duplicate === 1 ? "" : "s"} suppressed` : null, suppressed ? `${suppressed} suppressed` : null].filter(Boolean);
  if (uncertain) details.push("delivery could not be confirmed; reconciliation required");
  else if (failed) details.push("delivery failed");
  else if (noRecipients) details.push("no opted-in recipients");
  else if (entries.some(isSkipped)) details.push("delivery skipped");
  else if (entries.some((entry) => entry?.reason === "no_ar_customers")) details.push("no AR customers");
  else if (entries.some((entry) => entry?.reason === "no_recipients")) details.push("no recipients");
  const status = uncertain || failed ? "error" : (knownSent && !sent) || duplicate || suppressed || entries.some(isSkipped) ? "info" : "success";
  return {
    status,
    sent: knownSent ? sent : null,
    completed: !uncertain && !failed,
    message: details.length ? `${label}: ${details.join("; ")}.` : result?.message || `${label}: request completed.`,
  };
};
